import {
  Box,
  Breadcrumbs,
  Link as MUILink,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { useBreadcrumbs } from "../hooks/useBreadcrumbs";
import { type BreadcrumbEndAdornment, type BreadcrumbStartIcon } from "../providers/breadcrumbs";
import { usePreviewFeature } from "../providers/previewFeature";
import { PREVIEW_ICON_FONT_SIZE, PREVIEW_ICON_GAP } from "./PreviewFeature/constants";
import { PreviewFeatureIndicator } from "./PreviewFeature/PreviewFeatureIndicator";
import ScoutIcon from "./ScoutIcon";
import { ScoutTierChip } from "./ScoutTierChip";

function renderBreadcrumbStartIcon(startIcon: BreadcrumbStartIcon | undefined): ReactNode {
  if (startIcon === "scout") {
    return <ScoutIcon sx={{ fontSize: "1.25rem", flexShrink: 0 }} />;
  }
  return null;
}

function renderBreadcrumbEndAdornment(endAdornment: BreadcrumbEndAdornment | undefined): ReactNode {
  if (endAdornment === "scout-tier-pro") {
    return <ScoutTierChip tier="pro" />;
  }
  if (endAdornment === "scout-tier-regulatory-advisor") {
    return <ScoutTierChip tier="regulatory-advisor" />;
  }
  return null;
}

export default function NavBreadcrumbs() {
  const { breadcrumbs } = useBreadcrumbs();
  const { isPreview, activeVariant } = usePreviewFeature();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [fontSize, setFontSize] = useState("1.25rem");
  const breadcrumbsRef = useRef(null);

  useEffect(() => {
    const adjustFontSize = () => {
      if (breadcrumbsRef.current) {
        const maxHeight = 60;

        if ((breadcrumbsRef.current as HTMLDivElement).offsetHeight > maxHeight) {
          setFontSize("1.25rem");
        } else {
          setFontSize(isLargeScreen ? "1.5rem" : "1.25rem");
        }
      }
    };
    adjustFontSize();
  }, [breadcrumbs, isLargeScreen]);

  const displayBreadcrumbs = useMemo(() => {
    return isLargeScreen ? breadcrumbs : breadcrumbs.slice(-1);
  }, [isLargeScreen, breadcrumbs]);

  return (
    <Breadcrumbs
      ref={breadcrumbsRef}
      aria-label="breadcrumb"
      separator={"/"}
      sx={{
        maxHeight: "100%",
        overflow: "hidden",
        maxWidth: "100%",
      }}
    >
      {displayBreadcrumbs.map((breadcrumb, idx) => {
        const isTerminalCrumb = idx === displayBreadcrumbs.length - 1;
        const showPreviewSuffix = isPreview && isTerminalCrumb;

        if (!breadcrumb.label) {
          return <Skeleton key={`skeleton-${idx}`} width={200} />;
        }

        const startIcon = renderBreadcrumbStartIcon(breadcrumb.startIcon);
        const endAdornment = renderBreadcrumbEndAdornment(breadcrumb.endAdornment);
        const crumbGap = startIcon || endAdornment || showPreviewSuffix ? PREVIEW_ICON_GAP : 0;

        if (breadcrumb.to && !isTerminalCrumb) {
          return (
            <MUILink
              key={breadcrumb.label}
              underline="hover"
              color="primary"
              sx={{
                fontSize,
                textDecoration: "none",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                display: "flex",
                alignItems: "center",
                gap: crumbGap,
                minWidth: 0,
              }}
              to={`${breadcrumb.to}`}
              component={Link}
            >
              {startIcon}
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {breadcrumb.label}
              </Box>
              {endAdornment}
            </MUILink>
          );
        }

        return (
          <Typography
            component="div"
            sx={{
              fontSize,
              textDecoration: "none",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: crumbGap,
              minWidth: 0,
            }}
            key={breadcrumb.label}
          >
            {startIcon}
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {breadcrumb.label}
              {breadcrumb.addendum && (
                <span style={{ color: theme.palette.grey[500] }}>
                  {" "}
                  {`- ${breadcrumb.addendum}`}
                </span>
              )}
            </Box>
            {endAdornment}
            {showPreviewSuffix && (
              <PreviewFeatureIndicator
                fontSize={PREVIEW_ICON_FONT_SIZE}
                inline
                color={theme.palette.secondary.main}
                variant={activeVariant}
              />
            )}
          </Typography>
        );
      })}
    </Breadcrumbs>
  );
}
